import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { toast } from "sonner";
import { EmailVerificationDialog } from "@/components/EmailVerificationDialog";
import { AuthFormFields } from "@/components/auth/AuthFormFields";
import { AuthSuggestions } from "@/components/auth/AuthSuggestions";
import { TurnstileWidget } from "@/components/auth/TurnstileWidget";
import { useAuthForm } from "@/components/auth/useAuthForm";

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const AuthModal = ({ isOpen, onClose, onSuccess }: AuthModalProps) => {
  const {
    isLogin,
    setIsLogin,
    email,
    setEmail,
    password,
    setPassword,
    displayName,
    setDisplayName,
    loading,
    errors,
    setTurnstileToken,
    turnstileWidgetId,
    setTurnstileWidgetId,
    suggestions,
    loadingSuggestions,
    handleGetSuggestions,
    selectSuggestion,
    showVerificationDialog,
    setShowVerificationDialog,
    unverifiedEmail,
    handleTurnstileError,
    handleAuthSubmit,
    handleMagicLink,
    resetForm,
  } = useAuthForm();

  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const isEmailVerification = hashParams.get("access_token") || hashParams.get("type") === "recovery";

    if (isEmailVerification && isOpen) {
      toast.success("Email verified successfully!");
    }

    if (!isOpen) {
      resetForm();
    }
  }, [isOpen, resetForm]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <CardHeader className="pb-2 pt-4">
          <CardTitle>{isLogin ? "Sign In" : "Sign Up"}</CardTitle>
          {isLogin && <CardDescription>Welcome back!</CardDescription>}
        </CardHeader>

        <CardContent className="pt-4">
          <form
            onSubmit={async (event) => {
              event.preventDefault();
              const result = await handleAuthSubmit();
              if (result.flow === "login" && result.status === "success") {
                onSuccess?.();
                onClose();
              }
              if (result.flow === "signup" && (result.status === "success" || result.status === "existing-user")) {
                onClose();
              }
            }}
            className="space-y-3"
          >
            <AuthFormFields
              isLogin={isLogin}
              email={email}
              password={password}
              displayName={displayName}
              errors={errors}
              onEmailChange={setEmail}
              onPasswordChange={setPassword}
              onDisplayNameChange={setDisplayName}
              onGetSuggestions={handleGetSuggestions}
              loadingSuggestions={loadingSuggestions}
            />
            {!isLogin && <AuthSuggestions suggestions={suggestions} onSelect={selectSuggestion} />}
            {!isLogin && (
              <TurnstileWidget
                isActive={!isLogin && isOpen}
                widgetId={turnstileWidgetId}
                setWidgetId={setTurnstileWidgetId}
                onTokenChange={setTurnstileToken}
                onError={handleTurnstileError}
              />
            )}

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Loading..." : isLogin ? "Sign In" : "Sign Up"}
            </Button>
          </form>

          {isLogin && (
            <>
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">Or</span>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={async () => {
                  const success = await handleMagicLink();
                  if (success) {
                    onClose();
                  }
                }}
                disabled={loading || !email}
              >
                Send Magic Link
              </Button>
            </>
          )}

          <div className="mt-2 text-center">
            <Button variant="ghost" onClick={() => setIsLogin(!isLogin)} className="text-sm h-8">
              {isLogin ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
            </Button>
          </div>
        </CardContent>
      </DialogContent>

      <EmailVerificationDialog
        isOpen={showVerificationDialog}
        onClose={() => setShowVerificationDialog(false)}
        email={unverifiedEmail}
      />
    </Dialog>
  );
};

export default AuthModal;
